import { Injectable } from '@angular/core';
import { Brand } from 'src/app/components/company/models/new/brand.model';
import { Category } from '../models/new/category';
import { Gender } from '../models/new/style';
import { StyleDescription } from '../models/new/style-description';
import { StyleGroup } from '../models/new/style-group';
import { Collection } from '../models/new/collection';
import { Purpose } from '../models/new/purpose';
import { Team } from '../models/new/team';
import { Status } from '../models/new/status';
import { PriceChange, StylePrice, StylePriceFilters } from '../models/new/style-price';
import { Order } from '../models/new/order';
import _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class StylePriceMapper {
  toStylePrice(_style: any): StylePrice {
    // TODO: remove mock – replace with real priceChanges from API
    const rangeStart = new Date('2026-08-01').getTime();
    const rangeEnd = new Date('2026-12-31').getTime();
    const base = 50 + Math.random() * 100;
    const mockChanges = Array.from(
      { length: 20 },
      () =>
        new PriceChange({
          orderId: Math.floor(Math.random() * 90000) + 10000,
          orderCreationDate: new Date(rangeStart + Math.random() * (rangeEnd - rangeStart)),
          price: +(base + Math.random() * 20).toFixed(2)
        })
    ).sort((a, b) => a.orderCreationDate!.getTime() - b.orderCreationDate!.getTime());
    const mockAverage = +(mockChanges.reduce((s, p) => s + (p.price ?? 0), 0) / mockChanges.length).toFixed(2);

    return new StylePrice({
      brand: new Brand({
        id: _style.brand.id,
        name: _style.brand.name
      }),
      category: new Category({
        id: _style.category.id,
        name: _style.category.name
      }),

      modelId: _style.modelId,
      code: _style.code,
      name: _style.name,
      styleGroup: _style.styleGroup
        ? new StyleGroup({
            id: _style.styleGroup.id,
            name: _style.styleGroup.name
          })
        : undefined,
      styleDescription: _style.styleDescription
        ? new StyleDescription({
            id: _style.styleDescription.id,
            name: _style.styleDescription.name
          })
        : undefined,
      gender: _style.gender
        ? new Gender({
            id: _style.gender.id,
            name: _style.gender.name.toLowerCase().replace(/^\w/, (c: string) => c.toUpperCase())
          })
        : undefined,

      // TODO: remove mock – replace with real priceChanges from API
      priceChanges: mockChanges,
      periodAverage: mockAverage
    });
  }

  filtersToUrl(filters: StylePriceFilters) {
    return {
      brands: filters.brands ? filters.brands.map((brand) => brand.id) : undefined,
      categories: filters.categories ? filters.categories.map((category) => category.id) : undefined,
      styleGroups: filters.styleGroups ? filters.styleGroups.map((styleGroup) => styleGroup.id) : undefined,
      styleDescriptions: filters.styleDescriptions
        ? filters.styleDescriptions.map((styleDescription) => styleDescription.id)
        : undefined,
      collections: filters.collections ? filters.collections.map((collection) => collection.id) : undefined,
      purposes: filters.purposes ? filters.purposes.map((purpose) => purpose.id) : undefined,
      genders: filters.genders ? filters.genders.map((gender) => gender.id) : undefined,
      teams: filters.teams ? filters.teams.map((team) => team.id) : undefined,
      styleCode: filters.styleCode || undefined,
      statuses: filters.statuses ? filters.statuses.map((status) => status.id) : undefined,
      dateFrom: filters.dateRange?.from ? filters.dateRange.from.toISOString().split('T')[0] : undefined,
      dateTo: filters.dateRange?.to ? filters.dateRange.to.toISOString().split('T')[0] : undefined,
      page: filters.page || 1,
      size: filters.size || 20
    };
  }

  urlToFilters(url: any): StylePriceFilters {
    return new StylePriceFilters({
      brands: url.brands
        ? Array.isArray(url.brands)
          ? url.brands.map((id: any) => new Brand({ id: Number(id) }))
          : [new Brand({ id: Number(url.brands) })]
        : undefined,
      categories: url.categories
        ? Array.isArray(url.categories)
          ? url.categories.map((id: any) => new Category({ id: Number(id) }))
          : [new Category({ id: Number(url.categories) })]
        : undefined,
      styleGroups: url.styleGroups
        ? Array.isArray(url.styleGroups)
          ? url.styleGroups.map((id: any) => new StyleGroup({ id: Number(id) }))
          : [new StyleGroup({ id: Number(url.styleGroups) })]
        : undefined,
      styleDescriptions: url.styleDescriptions
        ? Array.isArray(url.styleDescriptions)
          ? url.styleDescriptions.map((id: any) => new StyleDescription({ id: Number(id) }))
          : [new StyleDescription({ id: Number(url.styleDescriptions) })]
        : undefined,
      collections: url.collections
        ? Array.isArray(url.collections)
          ? url.collections.map((id: any) => new Collection({ id: Number(id) }))
          : [new Collection({ id: Number(url.collections) })]
        : undefined,
      purposes: url.purposes
        ? Array.isArray(url.purposes)
          ? url.purposes.map((id: any) => new Purpose({ id: Number(id) }))
          : [new Purpose({ id: Number(url.purposes) })]
        : undefined,
      genders: url.genders
        ? Array.isArray(url.genders)
          ? url.genders.map((id: any) => new Gender({ id: Number(id) }))
          : [new Gender({ id: Number(url.genders) })]
        : undefined,
      teams: url.teams
        ? Array.isArray(url.teams)
          ? url.teams.map((id: any) => new Team({ id: Number(id) }))
          : [new Team({ id: Number(url.teams) })]
        : undefined,
      statuses: url.statuses
        ? Array.isArray(url.statuses)
          ? url.statuses.map((id: any) => new Status({ id: Number(id) }))
          : [new Status({ id: Number(url.statuses) })]
        : undefined,
      styleCode: url.styleCode || undefined,
      dateRange:
        url.dateFrom || url.dateTo
          ? {
              from: url.dateFrom ? new Date(url.dateFrom) : undefined,
              to: url.dateTo ? new Date(url.dateTo) : undefined
            }
          : undefined,
      page: Number(url.page || 1),
      size: Number(url.size || 20)
    });
  }

  toWhereClause(filters: StylePriceFilters) {
    return {
      brand: filters.brands?.length ? { id: { in: filters.brands.map((b) => b.id) } } : undefined,
      category: filters.categories?.length ? { id: { in: filters.categories.map((c) => c.id) } } : undefined,
      code: filters.styleCode ? { contains: filters.styleCode } : undefined,
      styleGroup: filters.styleGroups?.length ? { id: { in: filters.styleGroups.map((sg) => sg.id) } } : undefined,
      styleDescription: filters.styleDescriptions?.length
        ? { id: { in: filters.styleDescriptions.map((sd) => sd.id) } }
        : undefined,
      ...(filters.collections?.length && {
        schema: { some: { collection: { id: { in: filters.collections.map((col) => col.id) } } } }
      }),
      ...(filters.teams?.length && {
        schema: { some: { schemaTeamColors: { some: { team: { id: { in: filters.teams.map((t) => t.id) } } } } } }
      }),
      purpose: filters.purposes?.length ? { id: { in: filters.purposes.map((p) => p.id) } } : undefined,
      gender: filters.genders?.length ? { id: { in: filters.genders.map((g) => g.id) } } : undefined,
      ...(filters.statuses?.length && {
        currentStyleStatus: { some: { styleStatus: { id: { in: filters.statuses.map((s) => s.id) } } } }
      })
    };
  }
}
